<template>
  <div class="activity-table">
    <!-- Skeleton Loader -->
    <div v-if="loading" class="skeleton-container">
      <!-- Header Skeleton -->
      <div class="activity-table-header d-flex align-items-center w-100 flex-wrap" style="gap: 8px">
        <div class="flex-grow-1">
          <div class="row g-2 align-items-center">
            <div class="col-auto">
              <div class="skeleton-input skeleton-search"></div>
            </div>
            <div class="col-auto">
              <div class="skeleton-input skeleton-select"></div>
            </div>
            <div class="col-auto">
              <div class="skeleton-input skeleton-date"></div>
            </div>
          </div>
        </div>
        <div class="d-flex align-items-center ms-auto pe-0">
          <div class="skeleton-button skeleton-export"></div>
        </div>
      </div>

      <!-- Table Skeleton -->
      <div class="skeleton-table">
        <!-- Table Header -->
        <div class="skeleton-table-header">
          <div class="skeleton-cell skeleton-cell-header" v-for="i in 5" :key="i"></div>
        </div>
        <!-- Table Rows -->
        <div class="skeleton-table-row" v-for="i in 5" :key="i">
          <div class="skeleton-cell" v-for="j in 5" :key="j"></div>
        </div>
      </div>
    </div>

    <!-- Actual Content -->
    <div v-else>
      <div class="activity-table-header">
        <div class="activity-filters">
          <div class="search-pill">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="11" cy="11" r="7" stroke="#667085" stroke-width="1.5" />
              <path d="M20 20l-3.5-3.5" stroke="#667085" stroke-width="1.5" stroke-linecap="round" />
            </svg>
            <input type="text" placeholder="Search Ref ID" v-model="search" />
          </div>
          <FilterDropdown v-model="types" label="Type" all-label="All Type" :options="typeOptions" :width="140" />
          <FilterDropdown v-model="sourceOfFund" label="Source of Fund" all-label="All Source"
            :options="sourceOfFundOptions" :width="160" />
          <div class="date-pill">
            <SwiftDatePicker v-model="dateRange" placeholder="DD/MM/YY - DD/MM/YY" teleport />
          </div>
        </div>
        <div class="export-btn-responsive">
          <argon-button variant="export" size="sm" @click="exportToCSV">
            Export
          </argon-button>
        </div>
      </div>

      <SwiftDataTable :headers="columns" :items="filteredActivities" :search-value="search"
        :rows-per-page="pagination.per_page" hide-footer>
        <template #item-amount="{ amount, type }">
          <span :class="amountClass(type)">{{ formatAmount(amount, type) }}</span>
        </template>

        <template #item-transaction_id="{ transaction }">
          {{ transaction && transaction.transaction_id ? transaction.transaction_id : '-' }}
        </template>

        <template #item-reference_id="{ transaction }">
          {{ transaction && transaction.reference_id ? transaction.reference_id : '-' }}
        </template>

        <template #item-source="{ source }">
          {{ typeLabel(source) }}
        </template>

        <template #item-app_type="{ app_type }">
          {{ sourceOfFundLabel(app_type) }}
        </template>

        <template #item-no_va="{ no_va }">
          {{ no_va ? no_va : '-' }}
        </template>

        <template #item-bank_name="{ bank_name }">
          {{ bank_name ? bank_name : '-' }}
        </template>

        <template #item-latest_balance="{ latest_balance }">
          IDR {{ formatNumber(latest_balance) }}
        </template>
      </SwiftDataTable>

      <!-- Pagination Controls -->
      <div class="pagination-controls" v-if="!loading && activities.length > 0">
        <div class="per-page-control">
          <span>Show</span>
          <select class="per-page-select" v-model="pagination.per_page" @change="onPerPageChange">
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
            <option :value="500">500</option>
          </select>
          <span>of {{ totalFiltered }} Items</span>
        </div>

        <nav aria-label="Balance history pagination">
          <ul class="pager">
            <li>
              <button class="pager-btn" aria-label="Previous page" :disabled="!hasPrevPage"
                @click="goToPage(pagination.current_page - 1)">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </button>
            </li>
            <li v-for="page in visiblePages" :key="page">
              <button class="pager-btn" :class="{ active: page === pagination.current_page }" @click="goToPage(page)">
                {{ page }}
              </button>
            </li>
            <li>
              <button class="pager-btn" aria-label="Next page" :disabled="!hasNextPage"
                @click="goToPage(pagination.current_page + 1)">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import ArgonButton from "@/components/ArgonButton.vue";
import SwiftDataTable from "@/components/SwiftDataTable.vue";
import SwiftDatePicker from "@/components/SwiftDatePicker.vue";
import FilterDropdown from "./FilterDropdown.vue";
import swal from "sweetalert2";
export default {
  name: "BalanceActivityTable",
  props: {
    activities: {
      type: Array,
      required: true,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    paginationData: {
      type: Object,
      default: () => ({
        current_page: 1,
        per_page: 20,
        total: 0,
        last_page: 1,
        next_page_url: null,
        prev_page_url: null
      })
    },
    initialFilters: {
      type: Object,
      default: () => ({})
    },
  },
  components: {
    ArgonButton,
    SwiftDataTable,
    SwiftDatePicker,
    FilterDropdown,
  },
  data() {
    // Set default date range to today (one day)
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    return {
      // dateRange with default one-day range (today)
      dateRange: [todayStart, todayEnd],
      search: "",
      types: [],
      sourceOfFund: [],
      initialFiltersApplied: false,
      searchDebounceTimer: null,
      pagination: {
        current_page: 1,
        per_page: 20,
        total: 0,
        last_page: 1,
        next_page_url: null,
        prev_page_url: null
      },
      typeOptions: [
        { text: "Transfer", value: "transfer" },
        { text: "Transfer Fee", value: "transfer fee" },
        { text: "Refund", value: "refund" },
        { text: "Top Up", value: "topup" },
        { text: "Overbooking", value: "overbooking" },
      ],
      sourceOfFundOptions: [
        { text: "Dashboard", value: "dashboard" },
        { text: "API", value: "api" },
      ],
      searchField: "",
      columns: [
        { text: "Ref ID", value: "transaction_uuid", sortable: true, },
        { text: "Transaction ID", value: "transaction_id", sortable: true },
        { text: "Reference ID", value: "reference_id", sortable: true },
        { text: "Date & Time", value: "recorded_at", sortable: true },
        { text: "Type", value: "source", sortable: true },
        // app_type: which balance the activity came from ('dashboard' | 'api')
        { text: "Source of Fund", value: "app_type", sortable: true },
        { text: "Bank", value: "bank_name", sortable: true },
        { text: "VA Number", value: "no_va", sortable: true },
        { text: "Total Amount", value: "amount", sortable: true },
        { text: "Ending Balance", value: "latest_balance", sortable: true },
      ],
    };
  },
  computed: {
    // Get filtered data for current page display (always use server-side data)
    filteredActivities() {
      return this.activities;
    },

    // Calculate display values for pagination info (always use server pagination)
    totalFiltered() {
      return this.pagination.total;
    },

    displayStart() {
      if (this.totalFiltered === 0) return 0;
      return Math.min((this.pagination.current_page - 1) * this.pagination.per_page + 1, this.totalFiltered);
    },

    displayEnd() {
      if (this.totalFiltered === 0) return 0;
      return Math.min(this.pagination.current_page * this.pagination.per_page, this.totalFiltered);
    },

    // Calculate if there are previous/next pages (always use server pagination)
    hasPrevPage() {
      return this.pagination.current_page > 1;
    },

    hasNextPage() {
      return !!this.pagination.next_page_url;
    },

    totalPages() {
      return this.pagination.last_page || Math.ceil(this.totalFiltered / this.pagination.per_page);
    },
    visiblePages() {
      const current = this.pagination.current_page;
      const last = this.pagination.last_page;
      const range = 2; // kiri & kanan (total 5 page)

      let start = Math.max(1, current - range);
      let end = Math.min(last, current + range);

      const pages = [];
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    },
  },
  watch: {
    paginationData: {
      handler(newVal) {
        if (newVal) {
          this.pagination = { ...newVal };
        }
      },
      immediate: true,
      deep: true
    },

    initialFilters: {
      handler(newFilters) {
        // Only process once to prevent double API calls
        if (this.initialFiltersApplied) {
          return;
        }

        // If there are actual filters to apply (from URL parameters)
        if (newFilters && Object.keys(newFilters).length > 0) {
          // Apply initial filters from URL parameters
          if (newFilters.search) {
            this.search = newFilters.search;
          }
          if (newFilters.types) {
            this.types = newFilters.types;
          }
          if (newFilters.sourceOfFund) {
            this.sourceOfFund = newFilters.sourceOfFund;
          }
          if (newFilters.dateRange) {
            this.dateRange = newFilters.dateRange;
          }
        }

        // Mark as applied and trigger server call with current filters
        // (either from URL params or default date filter)
        this.initialFiltersApplied = true;
        this.$nextTick(() => {
          this.triggerServerFilter();
        });
      },
      immediate: true,
      deep: true
    },

    // Reset to page 1 and trigger server-side filtering when filters change
    search: {
      handler(newVal, oldVal) {
        // Only trigger if this is a user change, not initial setting
        if (this.initialFiltersApplied && oldVal !== newVal) {
          // Clear existing debounce timer
          if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
          }

          // Set new debounce timer (500ms delay)
          this.searchDebounceTimer = setTimeout(() => {
            this.pagination.current_page = 1;
            this.triggerServerFilter();
          }, 500);
        }
      }
    },

    types: {
      handler(newVal, oldVal) {
        // Only trigger if this is a user change, not initial setting
        if (this.initialFiltersApplied && oldVal !== newVal) {
          this.pagination.current_page = 1;
          this.triggerServerFilter();
        }
      }
    },

    sourceOfFund: {
      handler(newVal, oldVal) {
        if (this.initialFiltersApplied && oldVal !== newVal) {
          this.pagination.current_page = 1;
          this.triggerServerFilter();
        }
      }
    },

    dateRange: {
      handler(newVal, oldVal) {
        // Only trigger if this is a user change, not initial setting
        if (this.initialFiltersApplied && oldVal !== newVal) {
          this.pagination.current_page = 1;
          this.triggerServerFilter();
        }
      },
      deep: true
    }
  },

  methods: {
    goToPage(page) {
        if (
          page < 1 ||
          page > this.pagination.last_page ||
          page === this.pagination.current_page
        ) return;

        this.$emit(
          "page-change",
          page,
          this.pagination.per_page,
          this.getCurrentFilters()
        );
      },
    // Trigger server-side filtering
    triggerServerFilter() {
      const filters = this.getCurrentFilters();
      this.$emit('page-change', this.pagination.current_page, this.pagination.per_page, filters);
    },

    formatAmount(amount, type) {
      if (type === "debit") {
        return `-IDR ${Math.abs(amount).toLocaleString("id-ID")}`;
      } else {
        return `+IDR ${Math.abs(amount).toLocaleString("id-ID")}`;
      }
    },
    typeLabel(source) {
      if (!source) return "-";
      let value = String(source).toLowerCase();
      // Legacy rows recorded before inserts were unified to "transfer"
      if (value === "single_transfer") value = "transfer";
      const option = this.typeOptions.find((o) => o.value === value);
      return option ? option.text : source;
    },
    sourceOfFundLabel(value) {
      if (!value) return "-";
      const option = this.sourceOfFundOptions.find((o) => o.value === String(value).toLowerCase());
      return option ? option.text : value;
    },
    formatCurrency(val) {
      return `IDR ${val.toLocaleString("id-ID")}`;
    },
    amountClass(type) {
      if (type === "debit") {
        return "amount-negative";
      } else {
        return "amount-positive";
      }
    },
    formatNumber(amount) {
      if (amount === null || amount === undefined) return "0";
      const num = Number(amount);
      if (isNaN(num)) return amount; // Return original if not a number (e.g. for error messages)
      return num.toLocaleString("id-ID"); // Using Indonesian locale for formatting like 120.000.000
    },
    stripTime(date) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    },
    // Pagination methods
    onPreviousPage() {
      if (this.pagination.current_page > 1) {
        this.$emit('page-change', this.pagination.current_page - 1, this.pagination.per_page);
      }
    },
    onNextPage() {
      if (this.pagination.current_page < this.pagination.last_page) {
        this.$emit('page-change', this.pagination.current_page + 1, this.pagination.per_page);
      }
    },
    onPerPageChange() {
      // Always emit to parent to handle server-side pagination
      this.$emit('page-change', 1, this.pagination.per_page, this.getCurrentFilters());
    },

    // Handle pagination for filtered data
    onPreviousPageFiltered() {
      if (this.hasPrevPage) {
        // Always use server-side pagination
        this.$emit('page-change', this.pagination.current_page - 1, this.pagination.per_page, this.getCurrentFilters());
      }
    },

    onNextPageFiltered() {
      if (this.hasNextPage) {
        // Always use server-side pagination
        this.$emit('page-change', this.pagination.current_page + 1, this.pagination.per_page, this.getCurrentFilters());
      }
    },

    // Get current filter values
    getCurrentFilters() {
      return {
        search: this.search,
        types: this.types,
        sourceOfFund: this.sourceOfFund,
        dateRange: this.dateRange
      };
    },
    exportToCSV() {
      // Check if there's any data to export
      if (!this.activities || this.activities.length === 0) {
        swal.fire("No data to export");
        return;
      }

      // Emit event to parent component to handle export with current filters
      const filters = this.getCurrentFilters();
      this.$emit('export-csv', filters);
    },
    // Helper method for parent component to format and download CSV
    downloadCSV(dataToExport, filename = null) {
      if (!dataToExport || dataToExport.length === 0) {
        swal.fire("No data to export");
        return;
      }

      // Define CSV headers
      const headers = [
        "Ref ID",
        "Transaction ID",
        "Reference ID",
        "VA Number",
        "Bank Name",
        "Date & Time",
        "Type",
        "Total Amount",
        "Ending Balance",
      ];

      // Convert data to CSV format
      const csvContent = [
        headers.join(","), // Header row
        ...dataToExport.map((item) =>
          [
            `"${item.transaction_uuid || ""}"`,
            `"${item.transaction && item.transaction.transaction_id ? item.transaction.transaction_id : "-"}"`,
            `"${item.transaction && item.transaction.reference_id ? item.transaction.reference_id : "-"}"`,
            `"${item.no_va || "-"}"`,
            `"${item.bank_name || "-"}"`,
            `"${item.recorded_at || ""}"`,
            `"${item.source || ""}"`,
            `"${this.formatAmount(item.amount, item.type)}"`,
            `"IDR ${this.formatNumber(item.latest_balance)}"`,
          ].join(",")
        ),
      ].join("\n");

      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);

        // Generate filename with current date and filters if not provided
        if (!filename) {
          const now = new Date();
          const dateStr = now.toISOString().split("T")[0]; // YYYY-MM-DD format
          const timeStr = now.toTimeString().split(" ")[0].replace(/:/g, ""); // HHMMSS format

          // Build filename with filter info
          let filterInfo = "";
          if (this.search) filterInfo += `_search-${this.search.substring(0, 10)}`;
          if (this.types.length) filterInfo += `_type-${this.types.join("-")}`;
          if (this.dateRange && this.dateRange.length === 2) {
            const startDate = this.dateRange[0] instanceof Date ? this.dateRange[0] : new Date(this.dateRange[0]);
            const endDate = this.dateRange[1] instanceof Date ? this.dateRange[1] : new Date(this.dateRange[1]);
            filterInfo += `_from-${startDate.toISOString().split("T")[0]}_to-${endDate.toISOString().split("T")[0]}`;
          }

          filename = `balance-activity${filterInfo}_${dateStr}_${timeStr}.csv`;
        }
        link.setAttribute("download", filename);

        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
  },
  beforeUnmount() {
    // Clean up debounce timer
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  }
};
</script>

<style scoped>
.activity-table {
  width: 100%;
  font-family: "IBM Plex Sans Devanagari", sans-serif;
}

.activity-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 24px;
}

.activity-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

/* Search pill */
.search-pill {
  width: 200px;
  height: 32px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px;
  border-radius: 100px;
  border: 1px solid #d0d5dd;
  background: #fff;
}

.search-pill:focus-within {
  border-color: #43bb75;
}

.search-pill input {
  border: none;
  outline: none;
  background: transparent;
  width: 100%;
  min-width: 0;
  font-size: 14px;
  color: #022326;
}

.search-pill input::placeholder {
  color: #667085;
}

/* Date range pill (overrides SwiftDatePicker input) */
.date-pill {
  width: 240px;
}

.date-pill :deep(.date-input-wrapper) {
  min-width: 0;
}

.date-pill :deep(.dp__input) {
  height: 32px;
  border-radius: 100px !important;
  border: 1px solid #d0d5dd !important;
  background: #fff !important;
  padding: 0 40px 0 16px !important;
  font-size: 14px !important;
  color: #022326;
}

.date-pill :deep(.dp__input::placeholder) {
  color: #667085;
}

/* Amount styles */
.activity-table :deep(.amount-positive) {
  color: #1570ef;
  font-weight: 400 !important;
}

.activity-table :deep(.amount-negative) {
  color: #f04438;
  font-weight: 400 !important;
}

/* Pagination */
.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 24px;
  border-top: 1px solid #eaecf0;
  font-size: 14px;
  color: #022326;
}

.per-page-control {
  display: flex;
  align-items: center;
  gap: 8px;
}

.per-page-select {
  height: 32px;
  padding: 0 28px 0 12px;
  border-radius: 4px;
  border: 1px solid #d0d5dd;
  background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M6 9l6 6 6-6' stroke='%23667085' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat right 8px center;
  appearance: none;
  font-size: 14px;
  color: #022326;
  cursor: pointer;
}

.pager {
  display: flex;
  gap: 8px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.pager-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border-radius: 4px;
  border: 1px solid #d0d5dd;
  background: #fff;
  color: #022326;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.pager-btn.active {
  border-color: #43bb75;
  color: #1c9762;
  font-weight: 600;
}

.pager-btn:disabled {
  color: #d0d5dd;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .activity-table-header {
    flex-direction: column;
    align-items: stretch;
  }

  .search-pill,
  .date-pill {
    width: 100%;
  }

  .export-btn-responsive {
    display: flex;
    justify-content: flex-end;
  }

  .pagination-controls {
    flex-direction: column;
    align-items: flex-start;
  }
}

/* Skeleton Loader Styles */
.skeleton-container {
  animation: pulse 1.5s ease-in-out infinite;
}

.skeleton-input,
.skeleton-button,
.skeleton-cell {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
}

.skeleton-search {
  width: 250px;
  height: 40px;
}

.skeleton-select {
  width: 150px;
  height: 40px;
}

.skeleton-date {
  width: 200px;
  height: 40px;
}

.skeleton-export {
  width: 80px;
  height: 40px;
}

.skeleton-table {
  margin-top: 24px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.skeleton-table-header {
  display: flex;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 12px;
  gap: 12px;
}

.skeleton-table-row {
  display: flex;
  border-bottom: 1px solid #e2e8f0;
  padding: 12px;
  gap: 12px;
}

.skeleton-table-row:last-child {
  border-bottom: none;
}

.skeleton-cell {
  flex: 1;
  height: 20px;
}

.skeleton-cell-header {
  height: 24px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }

  100% {
    background-position: 200% 0;
  }
}

@keyframes pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.8;
  }
}
</style>
